import { ReactionTypeT, StatusTypeT } from '@lotushealth/lotushealth_js_sdk/schemas';
import { Pencil } from 'lucide-react';

import {
  usePatientAllergiesQuery,
  usePatientFormularyQuery,
  usePreviewPriorAuthQuery,
} from '@/entities/rx';
import { toAllergyRowModel } from '@/features/rx-form/model/allergiesTableModel';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { Button } from '@/shared/ui/shadcn/button';
import { Input } from '@/shared/ui/shadcn/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/shadcn/select';

import { useAllergyEditing } from '../../model/useAllergyEditing';
import { useAllergySelection } from '../../model/useAllergySelection';
import { useAlternativeSwitcher } from '../../model/useAlternativeSwitcher';
import { usePatientFormularyParams } from '../../model/usePatientFormularyParams';
import { usePriorAuthBannerModel } from '../../model/usePriorAuthBannerModel';
import type { useRxWizardForm } from '../../model/useRxWizardForm';
import { BenefitsCoordinationPanel } from '../BenefitsCoordinationPanel';
import { PrescriberInfo } from '../PrescriberInfo';
import { PriorAuthBanner } from '../PriorAuthBanner';
import { AllergySelector } from '../selectors/AllergySelector';
import { RxDraftSummaryNode } from '../RxDraftSummaryNode';

type RxFormApi = ReturnType<typeof useRxWizardForm>['form'];

export interface ReviewStepProps {
  userId: string;
  form: RxFormApi;
  errorMessage?: string | null;
  /** Optional callback to navigate back to details step for editing. */
  onEditDetails?: () => void;
  readOnly?: boolean;
  /** Controlled expedited flag for PA initiation. */
  paExpedited?: boolean;
  /** Called when the clinician toggles the expedited checkbox. */
  onPaExpeditedChange?: (checked: boolean) => void;
  /**
   * Optional override pinning the formulary lookup to a specific
   * ``PatientEligibilityId``. Surfaced in the Details step's picker
   * and threaded through here so the Review step queries the same
   * coverage. ``null`` means the server picks one.
   */
  formularyEligibilityOverride?: number | null;
}

export function ReviewStep({
  userId,
  form,
  errorMessage,
  onEditDetails,
  readOnly = false,
  paExpedited,
  onPaExpeditedChange,
  formularyEligibilityOverride = null,
}: ReviewStepProps) {
  const values = form.state.values;
  const patientAllergiesQuery = usePatientAllergiesQuery(userId);
  const selection = useAllergySelection(userId);
  const allergies = patientAllergiesQuery.data ?? [];
  const {
    cancelEditing,
    draft,
    editError,
    editMutation,
    editingAllergyId,
    save,
    setDraft,
    startEditing,
  } = useAllergyEditing(userId);

  const medicationDisplayName =
    values.kind === 'coded'
      ? (values.selectedMedication?.DisplayName ?? values.selectedMedication?.DrugName ?? '—')
      : (values.compoundDescription ?? '—');
  const pharmacyDisplayName = values.selectedPharmacy?.StoreName ?? '';

  const priorAuthParams = usePriorAuthBannerModel(values);
  // Intentionally run the preview even when the review is read-only (execute-only
  // proposed-action flow). The banner is informational — clinicians need to see
  // the PA posture regardless of whether they can still edit the draft.
  const priorAuthPreviewQuery = usePreviewPriorAuthQuery({
    userId,
    params: priorAuthParams,
  });

  const formularyParams = usePatientFormularyParams(form, formularyEligibilityOverride);
  const formularyQuery = usePatientFormularyQuery({ userId, params: formularyParams });

  const previewData = priorAuthPreviewQuery.data;
  const formularyData = formularyQuery.data;

  const { selectAlternative, isSwitching } = useAlternativeSwitcher({ form, readOnly });

  // Prefer formulary data fetched specifically for the medication selection
  // (richer alternatives + copays); fall back to whatever rode along with
  // the PA-preview when the formulary endpoint hasn't responded yet.
  const formularyForPanel = formularyData?.formulary ?? previewData?.formulary;
  const payerForPanel = formularyData?.payerInformation ?? previewData?.payerInformation;

  return (
    <div className="flex flex-col gap-6">
      <PriorAuthBanner
        outcome={previewData?.outcome ?? null}
        isLoading={priorAuthPreviewQuery.isFetching}
        medicationDisplayName={medicationDisplayName}
        expedited={paExpedited}
        onExpeditedChange={onPaExpeditedChange}
      />

      <BenefitsCoordinationPanel
        payerInformation={payerForPanel}
        formulary={formularyForPanel}
        benefits={previewData?.benefits}
        medication={values.kind === 'coded' ? values.selectedMedication : null}
        onSelectAlternative={selectAlternative}
        isSelectingAlternative={isSwitching}
      />

      <PrescriberInfo />

      <RxDraftSummaryNode
        medicationDisplayName={medicationDisplayName}
        directions={values.directions}
        quantity={values.quantity}
        refills={values.refills}
        daysSupply={values.daysSupply}
        pharmacyDisplayName={pharmacyDisplayName}
        pharmacyNotes={values.pharmacyNotes}
        noSubstitutions={values.noSubstitutions}
        onEdit={readOnly ? undefined : onEditDetails}
      />

      <div className="space-y-3">
        <div className="flex flex-col gap-4">
          {readOnly ? null : <AllergySelector selection={selection} />}

          <div className="flex flex-col gap-2">
            {patientAllergiesQuery.isLoading ? (
              <div className="text-sm text-muted-foreground">Loading allergies…</div>
            ) : null}

            {patientAllergiesQuery.isError ? (
              <Alert variant="destructive">
                <AlertTitle>Could not load allergies</AlertTitle>
                <AlertDescription>
                  <div>Allergies could not be fetched from DoseSpot.</div>
                  <div className="pt-2">
                    <Button
                      type="button"
                      variant="secondary"
                      radius="pill"
                      onClick={() => void patientAllergiesQuery.refetch()}
                    >
                      Retry
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            ) : null}

            {!patientAllergiesQuery.isLoading &&
            !patientAllergiesQuery.isError &&
            allergies.length === 0 ? (
              <div className="text-sm text-muted-foreground">No allergies on file.</div>
            ) : null}

            {!patientAllergiesQuery.isLoading &&
            !patientAllergiesQuery.isError &&
            allergies.length > 0 ? (
              <div className="space-y-2 divide-y">
                {allergies.map((allergy, index) => {
                  const row = toAllergyRowModel(allergy, index);
                  const id =
                    allergy.PatientAllergyId == null ? null : String(allergy.PatientAllergyId);
                  const isEditing = id != null && id === editingAllergyId && draft != null;

                  return (
                    <div key={row.key} className="pt-2 first:pt-0">
                      <div className="mb-1.5 flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="truncate font-medium">{row.allergen}</div>
                        </div>
                        {isEditing && !readOnly ? (
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              radius="pill"
                              onClick={() => void save()}
                              disabled={editMutation.isPending}
                            >
                              {editMutation.isPending ? 'Saving…' : 'Save'}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              radius="pill"
                              onClick={cancelEditing}
                              disabled={editMutation.isPending}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : !readOnly ? (
                          <Button
                            type="button"
                            size="icon-sm"
                            variant="ghost"
                            radius="pill"
                            aria-label="Edit allergy"
                            disabled={allergy.PatientAllergyId == null}
                            onClick={() => startEditing(allergy)}
                          >
                            <Pencil className="size-4" aria-hidden />
                          </Button>
                        ) : null}
                      </div>

                      {editError && isEditing ? (
                        <div className="mb-2 text-xs text-destructive">{editError}</div>
                      ) : null}

                      <div className="grid grid-cols-1 gap-2 @md:grid-cols-2">
                        <div className="space-y-1">
                          <div className="text-[10px] tracking-[0.08em] text-muted-foreground uppercase">
                            Reaction
                          </div>
                          {isEditing ? (
                            <Input
                              value={draft.reaction}
                              onChange={(e) =>
                                setDraft({ ...draft, reaction: e.currentTarget.value })
                              }
                              placeholder="Optional"
                              disabled={editMutation.isPending}
                            />
                          ) : (
                            <div className="text-sm">{row.reaction}</div>
                          )}
                        </div>

                        <div className="space-y-1">
                          <div className="text-[10px] tracking-[0.08em] text-muted-foreground uppercase">
                            Type
                          </div>
                          {isEditing ? (
                            <Select
                              value={draft.reactionType}
                              onValueChange={(value) =>
                                setDraft({
                                  ...draft,
                                  reactionType: value as typeof draft.reactionType,
                                })
                              }
                              disabled={editMutation.isPending}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={ReactionTypeT.Allergy}>Allergy</SelectItem>
                                <SelectItem value={ReactionTypeT.AdverseReaction}>
                                  Adverse reaction
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <div className="text-sm">{row.reactionType}</div>
                          )}
                        </div>

                        <div className="space-y-1">
                          <div className="text-[10px] tracking-[0.08em] text-muted-foreground uppercase">
                            Status
                          </div>
                          {isEditing ? (
                            <Select
                              value={draft.statusType}
                              onValueChange={(value) =>
                                setDraft({ ...draft, statusType: value as typeof draft.statusType })
                              }
                              disabled={editMutation.isPending}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={StatusTypeT.Active}>Active</SelectItem>
                                <SelectItem value={StatusTypeT.Inactive}>Inactive</SelectItem>
                                <SelectItem value={StatusTypeT.Deleted}>Deleted</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <div className="text-sm">{row.status}</div>
                          )}
                        </div>

                        <div className="space-y-1">
                          <div className="text-[10px] tracking-[0.08em] text-muted-foreground uppercase">
                            Onset
                          </div>
                          {isEditing ? (
                            <Input
                              value={draft.onsetDate}
                              onChange={(e) =>
                                setDraft({ ...draft, onsetDate: e.currentTarget.value })
                              }
                              placeholder="YYYY-MM-DD (optional)"
                              disabled={editMutation.isPending}
                            />
                          ) : (
                            <div className="text-sm">{row.onsetDate}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {errorMessage ? (
        <Alert variant="destructive">
          <AlertTitle>Submission issue</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}
